/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React, { useState, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import {
    AdobeService,
    CookiePreferenceService,
    ScriptService,
    Footer,
    SiteNav,
    DataSource,
    TooltipContent,
    SiteGlobalStyles,
    HEADER_HEIGHT,
    DisclaimerModal,
    LoginModal,
    SearchModal,
    useLanguage,
    loadPlugins,
    GlobalContextProvider
} from "macu-com-components";
import * as MacuComComponents from "macu-com-components";
import { createRoot } from 'react-dom/client';
import cx from "classnames";
import PropTypes from "prop-types";
import { MDXProvider } from "@mdx-js/react";
import { Helmet } from "react-helmet";
import SEO from "./seo";
import { getHeadTrackingTags, getBodyTrackingTags } from "./Services/trackingTags";
import { getValueAsDelimitedList, getValueAsString, getValueAsJSON, getAllValues } from "./Services/customData";
import packageJson from "../../package.json";
import "../config";
import "../style.scss";
const { withDataProps, getApiData, processDataString, Status } = DataSource;
const wrappedComponents = withDataProps(MacuComComponents);
import { SettingsCodeEnum } from "./Services/SettingsCodeEnum";
import { renderReactGlobalSnippet, renderPendingReactElements } from "./Services/renderReactSetup";

const isGatsbyPreview = process.env.GATSBY_BUILD_IS_PREVIEW === true || process.env.GATSBY_BUILD_IS_PREVIEW === "true";
const deferLoadScripts = process.env.GATSBY_DEFER_LOAD_SCRIPTS === true || process.env.GATSBY_DEFER_LOAD_SCRIPTS === "true";

const Layout = ({ children, ...props }) => {
    const pageProps = props.pageContext.frontmatter;
    const [isHydrationRendering, setHydrationRendering] = useState(false);
    const { lang } = useLanguage(pageProps?.path);
    const utilityNavCode = lang === "es" ? SettingsCodeEnum.UTILITY_NAV_ES : SettingsCodeEnum.UTILITY_NAV_EN;
    const siteNavigationCode = lang === "es" ? SettingsCodeEnum.SITE_NAVIGATION_ES : SettingsCodeEnum.SITE_NAVIGATION_EN;

    const {
        launchdarklyFlags,
        allResponsiveImagesQuery,
        allSettingsQuery,
        allMdxQuery,
    } = useStaticQuery(graphql`
    query {
            launchdarklyFlags: allFeatureFlags {
                edges {
                    node {
                        value
                    }
                }
            }

            allResponsiveImagesQuery: allResponsiveImages {
                nodes {
                    value
                }
            }

            allSettingsQuery: allSettings {
                nodes {
                    CodeName
                    Properties {
                        value
                    }
                }
            }

            allMdxQuery: allMdx(filter: {frontmatter: {pageType: {eq: "blog"}}}) {
                edges {
                    next {
                        frontmatter {
                            blogTitle
                            path
                        }
                    }
                    previous {
                        frontmatter {
                            blogTitle
                            path
                        }
                    }
                    node {
                        frontmatter {
                            blogTitle
                            path
                        }
                    }
                }
            }
        }
    `);

    const responsiveImages = JSON.parse(allResponsiveImagesQuery.nodes[0].value) || {};
    console.log('responsiveImages', responsiveImages);
    const headTrackingTags = getValueAsString(allSettingsQuery, isGatsbyPreview ? SettingsCodeEnum.HEAD_TRACKING_TAGS_PREVIEW : SettingsCodeEnum.HEAD_TRACKING_TAGS);
    const bodyTrackingTags = getValueAsString(allSettingsQuery, isGatsbyPreview ? SettingsCodeEnum.BODY_TRACKING_TAGS_PREVIEW : SettingsCodeEnum.BODY_TRACKING_TAGS);
    const [siteNav, setSiteNav] = useState(getPreLoadedNavigation());
    const { content: headTrackingContent, loadScripts } = ScriptService(headTrackingTags, deferLoadScripts);

    function renderReact (element, type, props) {
        console.log(`Rendering React widget type ${type} for element:`, element);
        if (!type) {
            console.error("renderReact: No type specified for React component rendering.");
            return;
        }
        const Component = wrappedComponents[type];
        if (!Component) {
            console.error(`renderReact: No React component found for type: ${type}`);
            return;
        }
        const navRoot = createRoot(element);
        navRoot.render(
            <GlobalContextProvider
                settings={settings}
                responsiveImages={responsiveImages}
                pageProps={pageProps}
                blogProps={allMdxQuery.edges.find(edge => edge.node.frontmatter?.path === pageProps?.path)}>
                <Component {...props} />
            </GlobalContextProvider>
        );
    }

    useEffect(() => {
        CookiePreferenceService.preferenceReload();
        setHydrationRendering(true);
        AdobeService.applyAdobeTargets();
        loadPlugins();
        loadScripts();
        window.__renderReact = renderReact;
        renderPendingReactElements(renderReact);

        if (typeof window !== "undefined" && (process.env.GATSBY_NR_ENABLED === true || process.env.GATSBY_NR_ENABLED === "true")) {
            (async () => {
                // const { BrowserAgent } = await import("@newrelic/browser-agent/loaders/browser-agent");
                // const options = {
                //     init: {browser_monitoring: {enabled: true}, distributed_tracing: {enabled: true},privacy: {cookies_enabled: true},ajax: {deny_list: ["bam.nr-data.net"]}}, // NREUM.init
                //     info: {beacon: "bam.nr-data.net",errorBeacon: "bam.nr-data.net",licenseKey: process.env.GATSBY_NR_LICENSE_KEY,applicationID: process.env.GATSBY_NR_APPLICATION_ID,sa: 1}, // NREUM.info
                //     loader_config: {accountID: process.env.GATSBY_NR_ACCOUNT_ID,trustKey: process.env.GATSBY_NR_TRUST_KEY,agentID: process.env.GATSBY_NR_AGENT_ID,licenseKey: process.env.GATSBY_NR_LICENSE_KEY,applicationID: process.env.GATSBY_NR_APPLICATION_ID} // NREUM.loader_config
                // };

                // // The agent loader code executes immediately on instantiation.
                // new BrowserAgent(options);

                if (typeof window.newrelic == "object") {
                    console.info("NR: Setting custom attributes.");
                    newrelic.setCustomAttribute("gatsby_build_version", process.env.GATSBY_BUILD_VERSION || "");
                    newrelic.setCustomAttribute("gatsby_build_env", process.env.GATSBY_BUILD_ENV || "");
                    newrelic.setCustomAttribute("gatsby_is_preview", process.env.GATSBY_BUILD_IS_PREVIEW || "");
                    newrelic.setCustomAttribute("component_library_version", packageJson.dependencies["macu-com-components"]);
                } else {
                    console.warn("newrelic is not defined, unable to set custom attributes.");
                }
            })();
        } else {
            console.warn("New Relic is not enabled, skipping NR initialization.");
        }
    }, []);

    async function loadApiData() {
        try {
            const { apiData, tableEntries } = getValueAsJSON(allSettingsQuery, SettingsCodeEnum.GLOBAL_DATA_SOURCE);
            const apiResults = await getApiData(apiData);
            return {...apiResults, ...tableEntries };
        } catch (error) {
            console.error("error parsing allSettingsQuery for loadApiData", error);
            return {};
        }
    }

    // Quick sync rendering of mega menu with available custom table data at build time
    function getPreLoadedNavigation() {
        try {
            const navigationData = getValueAsString(allSettingsQuery, siteNavigationCode);
            const { tableEntries } = getValueAsJSON(allSettingsQuery, SettingsCodeEnum.GLOBAL_DATA_SOURCE);
            if (!tableEntries) return;
            const processedNavigationData = processDataString(tableEntries, navigationData, Status.LOADING);
            const queriedSiteNav = JSON.parse(processedNavigationData);
            return queriedSiteNav;
        } catch (error) {
            console.error("error parsing allSettingsQuery in preLoadNavigation", error);
            throw error;
        }
    }

    // Slower async re-render of mega menu requesting custom api data at execution time
    async function getLoadedNavigation() {
        try {
            const navigationData = getValueAsString(allSettingsQuery, siteNavigationCode);
            const allApiData = await loadApiData();
            const processedNavigationData = processDataString(allApiData, navigationData, Status.LOADED);
            const queriedSiteNav = JSON.parse(processedNavigationData);
            setSiteNav(queriedSiteNav);
        } catch (error) {
            console.error("error parsing allSettingsQuery in loadNavigation", error);
        }
    }

    useEffect(() => {
        getLoadedNavigation();
    }, [allSettingsQuery, lang]);

    const settings = getAllValues(allSettingsQuery);

    function createMetaTags(frontmatter) {
        const metas = [{
            name: "search",
            content: frontmatter?.search || "",
        }, {
            name: "title",
            content: frontmatter?.title || "",
        }, {
            name: "blogTitle",
            content: frontmatter?.blogTitle || "",
        }, {
            name: "path",
            content: frontmatter?.path || "",
        }, {
            name: "alias",
            content: frontmatter?.alias || "",
        }, {
            name: "searchExclude",
            content: frontmatter?.searchExclude || "",
        }, {
            name: "sitemapExclude",
            content: frontmatter?.sitemapExclude || "",
        }, {
            name: "buildExclude",
            content: frontmatter?.buildExclude || "",
        }, {
            name: "tags",
            content: frontmatter?.tags || "",
        }, {
            name: "featuredArticle",
            content: frontmatter?.featuredArticle || "false",
        }, {
            name: "imageSrc",
            content: frontmatter?.imageSrc || "",
        }, {
            name: "imageAlt",
            content: frontmatter?.imageAlt || "",
        }, {
            name: "thumbImageSrc",
            content: frontmatter?.thumbImageSrc || "",
        }, {
            name: "thumbImageAlt",
            content: frontmatter?.thumbImageAlt || "",
        }, {
            property: "og:image",
            content: frontmatter?.thumbImageSrc === "undefined" || frontmatter?.thumbImageSrc === undefined || frontmatter?.thumbImageSrc === "" ?
                "" :
                /^(https:\/\/)/i.test(frontmatter?.thumbImageSrc) ?
                    frontmatter?.thumbImageSrc :
                    (process.env.GATSBY_MEDIA_URL + frontmatter?.thumbImageSrc),
        }, {
            property: "og:type",
            content: frontmatter?.pageType == "blog" ? "article" : "website",
        }, {
            name: "timestamp",
            content: frontmatter?.timestamp || "",
        }, {
            name: "pageType",
            content: frontmatter?.pageType || "",
        }, {
            name: "blogCategory",
            content: frontmatter?.blogCategory || "",
        }, {
            name: "msapplication-TileColor",
            content: "#da532c",
        }, {
            name: "theme-color",
            content: "#ffffff",
        }];

        if (frontmatter?.searchExclude === "true") {
            metas.push({
                name: "robots",
                content: "noindex,nofollow",
            });
        }

        return metas;
    }

    return (
        <GlobalContextProvider
            settings={settings}
            pageProps={pageProps}
            blogProps={allMdxQuery.edges.find(edge => edge.node.frontmatter?.path === pageProps?.path)}
            featureFlags={launchdarklyFlags.edges[0].node.value}
            responsiveImages={responsiveImages}
        >
            <Helmet>
                <link href="https://use.typekit.net/xmd6xov.css" rel="stylesheet" type="text/css" />
                {getHeadTrackingTags(headTrackingContent)}
                {getHeadTrackingTags(renderReactGlobalSnippet())}
            </Helmet>
            <SEO
                title={pageProps?.title || ""}
                description={pageProps?.description}
                meta={createMetaTags(pageProps)}
                lang={lang}>
                <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
                <link rel="icon" type="image/png" sizes="48x48" href="/favicon-48x48.png" />
                <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
                <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
                <link rel="manifest" href="/site.webmanifest" />
                <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#b81237" />
                {pageProps?.canonicalUrl ? <link rel="canonical" href={pageProps?.canonicalUrl} /> : null}
            </SEO>
            <SiteGlobalStyles>
                <SiteNav
                    menu={siteNav}
                    mobileLoginPagePaths={getValueAsDelimitedList(allSettingsQuery, SettingsCodeEnum.MOBILE_PAGES_WITH_LOGIN)}
                    utilityNav={getValueAsJSON(allSettingsQuery, utilityNavCode)}/>
                {isHydrationRendering && <TooltipContent />}
                <DisclaimerModal />
                <LoginModal />
                <SearchModal />
                <div className={cx("page-content position-relative", props.className)} style={{zIndex: 10}}>
                    <MDXProvider components={wrappedComponents}>{children}</MDXProvider>
                </div>
                <Footer height={HEADER_HEIGHT} />
                <div id="modal-root" />
            </SiteGlobalStyles>
            {getBodyTrackingTags(bodyTrackingTags)}
        </GlobalContextProvider>
    );
};

Layout.propTypes = {
    children: PropTypes.node.isRequired,
};

export default Layout;
